// File: src/pages/chat/Chat.tsx

import { useRef, useState, useEffect } from "react";
import { Checkbox, Panel, DefaultButton, PrimaryButton, TextField, SpinButton, Slider, Modal, Text } from "@fluentui/react";
import readNDJSONStream from "ndjson-readablestream";

import styles from "./Chat.module.css";
import "./ChatSettings.css";

import {
    chatApi,
    configApi,
    getProductDetails,
    RetrievalMode,
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    ResponseMessage,
    VectorFieldOptions,
    GPT4VInput
} from "../../api";
import { getN8NSessionId, clearN8NSession } from "../../api/n8nAdapter";
import { getBackendUri } from "../../api/api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { WebCitationItem } from "../../components/Answer/WebCitations";
import { clearAllSelections } from "../../components/Cards/selectionStore";
import { QuestionInput, SendMeta } from "../../components/QuestionInput";
import { EXAMPLE_CATEGORIES, EXAMPLE_UI_COPY, getLocalizedText } from "../../components/Example/ExampleCategories";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { useLogin, getToken, isLoggedIn, requireAccessControl } from "../../authConfig";
import { VectorSettings } from "../../components/VectorSettings";
import { useMsal } from "@azure/msal-react";
import { TokenClaimsDisplay } from "../../components/TokenClaimsDisplay";
import { GPT4VSettings } from "../../components/GPT4VSettings";
import { AnalysisOverlay } from "../../components/AnalysisPanel/AnalysisOverlay";
import { ThemeToggle } from "../../components/ThemeToggle/ThemeToggle";
import { useTheme } from "../../contexts/ThemeContext";
import { useExampleLanguage } from "../../contexts/ExampleLanguageContext";
import { submitFeedback, prepareFeedbackData, type FeedbackClosure } from "../../api/feedback";
import { classifyAnswerClosure, type AnswerClosure } from "../../utils/answerClosure";
import { RATING_LABEL_VALUES, type RatingLabel } from "../../utils/ratingLabels";
import { RatingPrompt, type RatingDetails } from "../../components/RatingPrompt/RatingPrompt";
import { ProductThreadBar, ProductThreadIntro } from "../../components/ProductThread/ProductThread";
import { FeedbackToast } from "../../components/FeedbackToast/FeedbackToast";
import { getCurrentUser } from "../../components/utils/getCurrentUser";
import { PublicIcon } from "../../components/Icons/PublicIcon";
import { ModelType, ReasoningEffort, PriceSource, N8NConfig, SiteId, SITE_ID_LABELS } from "../../api/models";
import { writeClipboardText } from "../../utils/conversationCopy";
import { threadHostProductSku, threadRequestFields } from "../../utils/conversationThread";
import { currentHostSource, isVoiceEnabledForChannel, resolveClientChannel } from "../../utils/clientChannel";
import { normalizeHostContextField } from "../../utils/hostContext";
import { useVoiceAutoSend } from "../../contexts/VoiceAutoSendContext";
import { useChatPersistence } from "./useChatPersistence";
import { useRatingPrompt } from "./useRatingPrompt";
import { useConversationThread } from "./useConversationThread";

interface ModelConfig {
    enabled: boolean;
    allowUserOverride: boolean;
    availableModels: string[];
    defaultIntentModel: string;
    defaultResponseModel: string;
    defaultReasoningEffort: string;
}

interface ChatProps {
    isWidget?: boolean;
    branding?: {
        logo?: string;
        primaryColor?: string;
    };
    initialModels?: {
        intentModel?: string;
        responseModel?: string;
        reasoningEffort?: string;
    };
    lockedPriceSource?: PriceSource;
    priceSourceLock?: boolean;
    lockedSiteId?: SiteId;
    siteIdLock?: boolean;
    initialSiteId?: SiteId;
}

type PrintExportScope = "conversation" | "message";

const CHAT_ACTION_COPY = {
    copyConversation: {
        el: "Αντιγραφή μηνύματος",
        en: "Copy message"
    },
    copiedConversation: {
        el: "Αντιγράφηκε",
        en: "Copied"
    },
    stoppedResponse: {
        el: "Η απάντηση διακόπηκε από τον χρήστη.",
        en: "Response stopped by user."
    },
    printExport: {
        el: "Εκτύπωση / εξαγωγή PDF",
        en: "Print / export PDF"
    },
    printExportSubtitle: {
        el: "Επιλέξτε τι θα συμπεριληφθεί στην εκτύπωση.",
        en: "Choose what to include in the print output."
    },
    printSelectedMessage: {
        el: "Επιλεγμένο μήνυμα",
        en: "Selected message"
    },
    printConversation: {
        el: "Συνομιλία",
        en: "Conversation"
    },
    cancel: {
        el: "Ακύρωση",
        en: "Cancel"
    }
} as const;

// Extra room at the bottom of the message list while the rating prompt shows above the fixed input.
const RATING_PROMPT_RESERVED_PX = 96;

const Chat = ({ isWidget = false, branding, initialModels, lockedPriceSource, priceSourceLock, lockedSiteId, siteIdLock, initialSiteId }: ChatProps = {}) => {
    const processingMessage = "Processing question";
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0.3);
    const [retrieveCount, setRetrieveCount] = useState<number>(10);
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(false);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(true);
    const [vectorFieldList, setVectorFieldList] = useState<VectorFieldOptions[]>([VectorFieldOptions.Embedding]);
    const [useOidSecurityFilter, setUseOidSecurityFilter] = useState<boolean>(false);
    const [useGroupsSecurityFilter, setUseGroupsSecurityFilter] = useState<boolean>(false);
    const [gpt4vInput, setGpt4vInput] = useState<GPT4VInput>(GPT4VInput.TextAndImages);
    const [useGPT4V, setUseGPT4V] = useState<boolean>(false);

    // Model selection state
    const [intentModel, setIntentModel] = useState<string>(initialModels?.intentModel || ModelType.GPT4O);
    const [responseModel, setResponseModel] = useState<string>(initialModels?.responseModel || ModelType.GPT4O);
    const [reasoningEffort, setReasoningEffort] = useState<string>(initialModels?.reasoningEffort || ReasoningEffort.Low);
    const [modelConfig, setModelConfig] = useState<ModelConfig | null>(null);
    const [n8nConfig, setN8nConfig] = useState<N8NConfig | null>(null);
    // ENG-015: the mic stays hidden until GET /n8n-config answered (or failed — then it shows,
    // since an unreachable config must not remove a feature; /speech/token is the backstop).
    const [n8nConfigSettled, setN8nConfigSettled] = useState<boolean>(false);

    // New state for example visibility
    const [showExamples, setShowExamples] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    // Session ID for feedback tracking
    const [sessionId] = useState<string>(() => `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`);
    const [feedbackToast, setFeedbackToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

    // Use existing theme context
    const { theme, toggleTheme } = useTheme();
    const { exampleLanguage, toggleExampleLanguage } = useExampleLanguage();

    const scrollOnUserSubmitRef = useRef(false);
    const lastQuestionRef = useRef<string>("");
    const hydratedFromStorageRef = useRef<boolean>(false);
    const pendingAnchorIdRef = useRef<string | null>(null);
    const anchorRetryRef = useRef<number>(0);
    const abortControllerRef = useRef<AbortController | null>(null);
    const chatRootRef = useRef<HTMLDivElement | null>(null);

    const computeMessageId = (index: number, question: string): string => {
        // Simple deterministic id based on index and a tiny hash of question
        let hash = 0;
        for (const char of question) {
            hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
        }
        return `msg-${index}-${hash.toString(36)}`;
    };
    const chatScrollRef = useRef<HTMLDivElement | null>(null);
    const [isAtBottom, setIsAtBottom] = useState(true);

    const handleScroll = () => {
        if (!chatScrollRef.current) return;
        const { scrollHeight, scrollTop, clientHeight } = chatScrollRef.current;
        const atBottom = scrollHeight - scrollTop - clientHeight < 2;
        setIsAtBottom(atBottom);
    };

    const scrollToBottom = () => {
        if (!chatScrollRef.current) return;
        chatScrollRef.current.scrollTo({
            top: chatScrollRef.current.scrollHeight,
            behavior: "smooth"
        });
    };

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [answers, setAnswers] = useState<[string, ChatAppResponse][]>([]);
    const [copiedAnswerIndex, setCopiedAnswerIndex] = useState<number | null>(null);
    const [pendingPrintAnswerIndex, setPendingPrintAnswerIndex] = useState<number | null>(null);
    const [statusMessages, setStatusMessages] = useState<Map<number, { message: string; timestamp: number; fadingOut?: boolean }>>(new Map());
    const statusTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
    const [hasActiveStatus, setHasActiveStatus] = useState<Map<number, boolean>>(new Map());
    const bufferedContentRef = useRef<Map<number, string>>(new Map());
    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    // State for storing message metadata per answer
    const [answerMetadata, setAnswerMetadata] = useState<Map<number, { message?: string; timestamp: number; fadingOut?: boolean; hidden?: boolean }>>(
        new Map()
    );
    const metadataTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
    // Track the last message type per answer index (meta or content) to decide fade behavior on end events
    const lastMessageTypeRef = useRef<Map<number, "meta" | "content" | null>>(new Map());

    // Web research citations per answer index (sources surfaced via web_research's facts[])
    const [answerWebCitations, setAnswerWebCitations] = useState<Map<number, WebCitationItem[]>>(new Map());

    // State for question answers preview
    const [questionAnswers, setQuestionAnswers] = useState<string>("");
    const [clearTriggerId, setClearTriggerId] = useState<number>(0);

    // ENG-027: ask for a conversation rating only after an answer that closes the question.
    // ENG-054: in the widget a host product change is a thread switch, which disarms the prompt.
    const ratingPrompt = useRatingPrompt(isLoading || isStreaming, isWidget);

    // ENG-054: widget only, a conversation thread per product the host shows, next to the general one.
    const conversationThread = useConversationThread({ isWidget, busy: isLoading || isStreaming, hasMessages: answers.length > 0 });
    // ENG-054: a new message that arrived while its thread's history was still loading.
    const queuedRequestRef = useRef<{ question: string; sendMeta?: SendMeta; introSuggestion: boolean; introSuggestionId?: string } | null>(null);

    const {
        priceSource,
        setPriceSource,
        priceSourceLocked,
        siteId,
        setSiteId,
        siteIdLocked,
        historyThreadKey,
        recentThreads,
        productThreadTitle,
        rememberProductTitle,
        generalLastQuestion
    } = useChatPersistence({
        isWidget,
        lockedPriceSource,
        priceSourceLock,
        lockedSiteId,
        siteIdLock,
        initialSiteId,
        answers,
        setAnswers,
        answerWebCitations,
        setAnswerWebCitations,
        isStreaming,
        processingMessage,
        lastQuestionRef,
        hydratedFromStorageRef,
        resetRatingPrompt: ratingPrompt.reset,
        setSelectedAnswer,
        setQuestionAnswers,
        setClearTriggerId,
        threadKey: conversationThread.threadKey,
        productSku: conversationThread.productSku
    });
    const [pendingSiteId, setPendingSiteId] = useState<SiteId | null>(null);
    const [showSiteIdConfirm, setShowSiteIdConfirm] = useState(false);

    const requestSiteIdChange = (next: SiteId) => {
        if (siteIdLocked) return;
        if (answers.length > 0) {
            setPendingSiteId(next);
            setShowSiteIdConfirm(true);
            return;
        }
        setSiteId(next);
    };

    const confirmSiteIdChange = () => {
        if (!pendingSiteId) return;
        setSiteId(pendingSiteId);
        setPendingSiteId(null);
        setShowSiteIdConfirm(false);
    };

    const cancelSiteIdChange = () => {
        setPendingSiteId(null);
        setShowSiteIdConfirm(false);
    };

    const client = useLogin ? useMsal().instance : undefined;

    const [showGPT4VOptions, setShowGPT4VOptions] = useState<boolean>(false);
    const [showSemanticRankerOption, setShowSemanticRankerOption] = useState<boolean>(false);
    const [showVectorOption, setShowVectorOption] = useState<boolean>(false);
    const resolvedAppTitle = (n8nConfig?.appTitle || "").trim() || getLocalizedText(EXAMPLE_UI_COPY.assistantTitle, exampleLanguage);

    // Check if we're in empty state
    const isEmptyState = !lastQuestionRef.current;
    const shouldRenderInlineError = !!error && answers.at(-1)?.[1]?.choices?.[0]?.message?.content === processingMessage;

    const getConfig = async () => {
        // Widget behavior is supplied by its initialization config.
        if (isWidget) {
            setShowGPT4VOptions(false);
            setUseSemanticRanker(true);
            setShowSemanticRankerOption(true);
            setShowVectorOption(true);
            return;
        }

        const token = client ? await getToken(client) : undefined;

        const apiCall = configApi(token);
        apiCall.then(config => {
            setShowGPT4VOptions(config.showGPT4VOptions);
            // Don't auto-enable semantic ranker - keep default false, just show the option
            setShowSemanticRankerOption(config.showSemanticRankerOption);
            setShowVectorOption(config.showVectorOption);
            if (!config.showVectorOption) {
                setRetrievalMode(RetrievalMode.Text);
            }
        });
    };
    // After hydrating from storage, scroll to last restored message once the DOM paints
    useEffect(() => {
        if (!hydratedFromStorageRef.current || answers.length === 0) return;
        hydratedFromStorageRef.current = false;
        const lastIdx = answers.length - 1;
        const anchorId = computeMessageId(lastIdx, answers[lastIdx][0]);
        pendingAnchorIdRef.current = anchorId;
        anchorRetryRef.current = 0;

        const tryScroll = () => {
            if (!pendingAnchorIdRef.current) return;
            const el = document.querySelector(`[data-message-id="${pendingAnchorIdRef.current}"]`) as HTMLElement | null;
            const container = chatScrollRef.current;
            if (el && container) {
                const elRect = el.getBoundingClientRect();
                const contRect = container.getBoundingClientRect();
                const top = elRect.top - contRect.top + container.scrollTop;
                container.scrollTo({ top, behavior: "auto" });
                pendingAnchorIdRef.current = null;
                return;
            }
            // Retry up to ~1s
            if (anchorRetryRef.current < 10) {
                anchorRetryRef.current++;
                setTimeout(tryScroll, 100);
            } else {
                // Fallback: do nothing per requirement
                pendingAnchorIdRef.current = null;
            }
        };

        // Wait for paint
        const raf = requestAnimationFrame(() => setTimeout(tryScroll, 0));
        return () => cancelAnimationFrame(raf);
    }, [answers]);

    // Listen for a request from container to scroll to last message after un-minimize
    useEffect(() => {
        const handler = () => {
            if (answers.length === 0) return;
            const lastIdx = answers.length - 1;
            const anchorId = computeMessageId(lastIdx, answers[lastIdx][0]);
            const el = document.querySelector(`[data-message-id="${anchorId}"]`) as HTMLElement | null;
            const container = chatScrollRef.current;
            if (el && container) {
                const elRect = el.getBoundingClientRect();
                const contRect = container.getBoundingClientRect();
                const top = elRect.top - contRect.top + container.scrollTop;
                container.scrollTo({ top, behavior: "auto" });
            }
        };
        globalThis.addEventListener("public-widget:scroll-to-last", handler as EventListener);
        return () => globalThis.removeEventListener("public-widget:scroll-to-last", handler as EventListener);
    }, [answers]);

    const handleAsyncRequest = async (
        question: string,
        responseBody: ReadableStream<any>,
        newAnswerIndex: number,
        abortSignal: AbortSignal,
        requestStartTime: number,
        responseSessionState: any
    ): Promise<{ content: string; closure?: AnswerClosure } | null> => {
        let answerContent = "";
        let askResponse: ChatAppResponse = {
            choices: [
                {
                    index: 0,
                    message: { content: "", role: "assistant" },
                    context: { data_points: [], followup_questions: [], thoughts: [] },
                    session_state: responseSessionState
                }
            ]
        };
        let firstChunkReceived = false;
        let firstEventReceived = false;
        let firstMetadataReceived = false;
        const streamingStartTime = Date.now();

        // Initialize buffer for this answer
        bufferedContentRef.current.set(newAnswerIndex, "");

        try {
            setIsStreaming(true);

            for await (const event of readNDJSONStream(responseBody)) {
                // Check if request was aborted using the local signal reference
                if (abortSignal.aborted) {
                    break;
                }

                // Track when first event is received (any type)
                if (!firstEventReceived) {
                    firstEventReceived = true;
                }

                // Track when first content chunk is received
                if (!firstChunkReceived && event["choices"]?.[0]?.delta?.content) {
                    firstChunkReceived = true;
                    const timeToFirstChunk = Date.now() - streamingStartTime;
                    console.log(`[${new Date().toISOString()}] Time to first chunk: ${timeToFirstChunk}ms`);
                }
                if (event["choices"] && event["choices"][0]["context"] && event["choices"][0]["context"]["data_points"]) {
                    event["choices"][0]["message"] = event["choices"][0]["delta"];
                    askResponse = {
                        ...(event as ChatAppResponse),
                        choices: [
                            {
                                ...event["choices"][0],
                                session_state: {
                                    ...event["choices"][0]?.session_state,
                                    ...responseSessionState
                                }
                            }
                        ]
                    };

                    // End event received - only fade out metadata if the last message was content
                    const lastMessageType = lastMessageTypeRef.current.get(newAnswerIndex);
                    console.debug(`   🏁 [${new Date().toISOString()}] End event received, last message type was: ${lastMessageType || "none"}`);

                    if (lastMessageType === "content") {
                        // Last message was content, so ALWAYS attempt to fade out metadata
                        console.debug(`   🏁 [${new Date().toISOString()}] End after content - forcing fade-out`);

                        // Clear auto fade-out timer if it exists
                        const existingTimer = metadataTimersRef.current.get(newAnswerIndex);
                        if (existingTimer) {
                            console.debug(`   🧹 [${new Date().toISOString()}] Clearing auto-fade timer due to end event`);
                            clearTimeout(existingTimer);
                            metadataTimersRef.current.delete(newAnswerIndex);
                        }

                        // Fade out immediately (0.5 seconds) - will be no-op if metadata doesn't exist
                        const fadeOutDuration = 500; // 500ms fade out
                        setAnswerMetadata(prev => {
                            const updated = new Map(prev);
                            const current = updated.get(newAnswerIndex);
                            if (current && !current.fadingOut) {
                                const timeShown = Date.now() - current.timestamp;
                                console.debug(
                                    `   🔄 [${new Date().toISOString()}] End event: Setting metadata to fadingOut=true (shown ${timeShown}ms):`,
                                    current.message
                                );
                                updated.set(newAnswerIndex, { ...current, fadingOut: true });
                                setTimeout(() => {
                                    console.debug(`   🗑️  [${new Date().toISOString()}] End event: Removing metadata from state after fade:`, current.message);
                                    setAnswerMetadata(prev => {
                                        const updated = new Map(prev);
                                        updated.delete(newAnswerIndex);
                                        return updated;
                                    });
                                }, fadeOutDuration);
                            } else if (current && current.fadingOut) {
                                console.debug(`   ⏭️  [${new Date().toISOString()}] End event: Metadata already fading, no action needed`);
                            } else {
                                console.debug(`   ⏭️  [${new Date().toISOString()}] End event: No metadata to fade (likely already cleared by auto-fade)`);
                            }
                            return updated;
                        });
                    } else {
                        console.debug(`   🏁 [${new Date().toISOString()}] End after ${lastMessageType || "none"} - NOT fading out metadata`);
                    }

                    // Reset message type after processing end event
                    lastMessageTypeRef.current.set(newAnswerIndex, null);
                    console.debug(`   🔄 [${new Date().toISOString()}] Reset lastMessageType to null after end event`);
                } else if (event["choices"] && event["choices"][0]["delta"]["isWebCitations"]) {
                    // Web research citations — store per answer index, rendered alongside the answer
                    const items = event["choices"][0]["delta"]["items"];
                    if (Array.isArray(items) && items.length > 0) {
                        console.debug(`📎 [${new Date().toISOString()}] UI received ${items.length} web citation(s)`);
                        setAnswerWebCitations(prev => {
                            const updated = new Map(prev);
                            const existing = updated.get(newAnswerIndex) ?? [];
                            updated.set(newAnswerIndex, [...existing, ...items]);
                            return updated;
                        });
                    }
                } else if (event["choices"] && event["choices"][0]["delta"]["isMetadata"]) {
                    // This is a metadata event with emoji and/or message
                    const delta = event["choices"][0]["delta"];
                    const now = Date.now();
                    if (!firstMetadataReceived) {
                        firstMetadataReceived = true;
                        const timeToFirstMeta = now - streamingStartTime;
                        console.log(`[${new Date().toISOString()}] Time to first metadata: ${timeToFirstMeta}ms — "${delta.message}"`);
                    }
                    console.debug(`📊 [${new Date().toISOString()}] UI received metadata:`, delta, `(t=${now})`);

                    // Track that the last message type was meta
                    lastMessageTypeRef.current.set(newAnswerIndex, "meta");

                    const fadeOutDuration = 500; // 500ms fade out duration
                    const minDisplayTime = 1000; // 1000ms (1 second) minimum display time

                    // Clear any existing auto fade-out timer
                    const existingTimer = metadataTimersRef.current.get(newAnswerIndex);
                    if (existingTimer) {
                        console.log(`   🧹 [${new Date().toISOString()}] Clearing existing auto-fade timer for new metadata:`, delta.message);
                        clearTimeout(existingTimer);
                        metadataTimersRef.current.delete(newAnswerIndex);
                    }

                    setAnswerMetadata(prev => {
                        const updated = new Map(prev);
                        const existing = updated.get(newAnswerIndex);
                        const now = Date.now();

                        if (!existing || existing.fadingOut) {
                            // No existing metadata or it's fading - show immediately
                            if (!existing) {
                                console.debug(`   ✅ [${new Date().toISOString()}] Showing new metadata immediately (no existing):`, delta.message);
                            } else {
                                console.debug(`   ✅ [${new Date().toISOString()}] Showing new metadata immediately (previous was fading):`, delta.message);
                            }
                            updated.set(newAnswerIndex, {
                                message: delta.message,
                                timestamp: now,
                                fadingOut: false,
                                hidden: false
                            });
                            console.debug(
                                `   📝 [${new Date().toISOString()}] Metadata state: SET (message="${delta.message}", fadingOut=false, timestamp=${now})`
                            );

                            // Schedule auto fade-out after 10 seconds
                            console.debug(`   ⏰ [${new Date().toISOString()}] Scheduling auto-fade timer for 10 seconds:`, delta.message);
                            const autoFadeTimer = setTimeout(() => {
                                console.log(`   ⏰ [${new Date().toISOString()}] Auto-fade timer triggered (10s elapsed):`, delta.message);
                                setAnswerMetadata(prev => {
                                    const updated = new Map(prev);
                                    const current = updated.get(newAnswerIndex);
                                    if (current && !current.fadingOut) {
                                        const timeShown = Date.now() - current.timestamp;
                                        console.log(
                                            `   🔄 [${new Date().toISOString()}] Auto-fade: Setting metadata to fadingOut=true (shown ${timeShown}ms):`,
                                            current.message
                                        );
                                        updated.set(newAnswerIndex, { ...current, fadingOut: true });
                                        setTimeout(() => {
                                            console.log(
                                                `   🗑️  [${new Date().toISOString()}] Auto-fade: Removing metadata from state after fade:`,
                                                current.message
                                            );
                                            setAnswerMetadata(prev => {
                                                const updated = new Map(prev);
                                                const current = updated.get(newAnswerIndex);
                                                if (current) {
                                                    updated.set(newAnswerIndex, { ...current, fadingOut: false, hidden: true });
                                                }
                                                return updated;
                                            });
                                        }, fadeOutDuration);
                                    }
                                    return updated;
                                });
                            }, 10000);
                            metadataTimersRef.current.set(newAnswerIndex, autoFadeTimer);

                            return updated;
                        }

                        // There's existing visible metadata - check if minimum time has passed
                        const timeSinceShown = now - existing.timestamp;

                        if (timeSinceShown >= minDisplayTime) {
                            // Minimum time passed - replace immediately with quick fade
                            console.log(
                                `   ⏩ [${new Date().toISOString()}] Min time passed (${timeSinceShown}ms), replacing:`,
                                existing.message,
                                "→",
                                delta.message
                            );
                            console.log(`   🔄 [${new Date().toISOString()}] Setting old metadata to fadingOut=true:`, existing.message);
                            updated.set(newAnswerIndex, { ...existing, fadingOut: true });

                            // Show new after quick fade
                            setTimeout(() => {
                                console.log(`   🗑️  [${new Date().toISOString()}] Removing old metadata from state after fade:`, existing.message);
                                console.log(`   ✅ [${new Date().toISOString()}] Showing new metadata after fade:`, delta.message);
                                const newTimestamp = Date.now();
                                setAnswerMetadata(prev => {
                                    const updated = new Map(prev);
                                    updated.set(newAnswerIndex, {
                                        message: delta.message,
                                        timestamp: newTimestamp,
                                        fadingOut: false,
                                        hidden: false
                                    });
                                    return updated;
                                });
                                console.log(
                                    `   📝 [${new Date().toISOString()}] Metadata state: REPLACED (message="${
                                        delta.message
                                    }", fadingOut=false, timestamp=${newTimestamp})`
                                );

                                // Schedule auto fade-out
                                console.debug(`   ⏰ [${new Date().toISOString()}] Scheduling auto-fade timer for 10 seconds:`, delta.message);
                                const autoFadeTimer = setTimeout(() => {
                                    console.log(`   ⏰ [${new Date().toISOString()}] Auto-fade timer triggered (10s elapsed):`, delta.message);
                                    setAnswerMetadata(prev => {
                                        const updated = new Map(prev);
                                        const current = updated.get(newAnswerIndex);
                                        if (current && !current.fadingOut) {
                                            const timeShown = Date.now() - current.timestamp;
                                            console.log(
                                                `   🔄 [${new Date().toISOString()}] Auto-fade: Setting metadata to fadingOut=true (shown ${timeShown}ms):`,
                                                current.message
                                            );
                                            updated.set(newAnswerIndex, { ...current, fadingOut: true });
                                            setTimeout(() => {
                                                console.log(
                                                    `   🗑️  [${new Date().toISOString()}] Auto-fade: Removing metadata from state after fade:`,
                                                    current.message
                                                );
                                                setAnswerMetadata(prev => {
                                                    const updated = new Map(prev);
                                                    const current = updated.get(newAnswerIndex);
                                                    if (current) {
                                                        updated.set(newAnswerIndex, { ...current, fadingOut: false, hidden: true });
                                                    }
                                                    return updated;
                                                });
                                            }, fadeOutDuration);
                                        }
                                        return updated;
                                    });
                                }, 10000);
                                metadataTimersRef.current.set(newAnswerIndex, autoFadeTimer);
                            }, fadeOutDuration);

                            return updated;
                        } else {
                            // Not enough time passed - wait for remaining time, then quick replace
                            const waitTime = minDisplayTime - timeSinceShown;
                            console.log(
                                `   ⏱️  [${new Date().toISOString()}] Waiting ${waitTime}ms before replacing (shown ${timeSinceShown}ms):`,
                                existing.message,
                                "→",
                                delta.message
                            );
                            console.log(`   ⏸️  [${new Date().toISOString()}] Keeping existing metadata visible during wait:`, existing.message);

                            setTimeout(() => {
                                console.log(`   ⏱️  [${new Date().toISOString()}] Wait complete, starting replacement:`, existing.message, "→", delta.message);
                                setAnswerMetadata(prev => {
                                    const updated = new Map(prev);
                                    const current = updated.get(newAnswerIndex);
                                    if (current && !current.fadingOut) {
                                        console.log(`   🔄 [${new Date().toISOString()}] Setting old metadata to fadingOut=true:`, current.message);
                                        updated.set(newAnswerIndex, { ...current, fadingOut: true });
                                    }
                                    return updated;
                                });

                                // Show new after quick fade
                                setTimeout(() => {
                                    console.log(`   🗑️  [${new Date().toISOString()}] Removing old metadata from state after fade:`, existing.message);
                                    console.log(`   ✅ [${new Date().toISOString()}] Showing new metadata after fade:`, delta.message);
                                    const newTimestamp = Date.now();
                                    setAnswerMetadata(prev => {
                                        const updated = new Map(prev);
                                        updated.set(newAnswerIndex, {
                                            message: delta.message,
                                            timestamp: newTimestamp,
                                            fadingOut: false,
                                            hidden: false
                                        });
                                        return updated;
                                    });
                                    console.log(
                                        `   📝 [${new Date().toISOString()}] Metadata state: REPLACED (message="${
                                            delta.message
                                        }", fadingOut=false, timestamp=${newTimestamp})`
                                    );

                                    // Schedule auto fade-out
                                    console.debug(`   ⏰ [${new Date().toISOString()}] Scheduling auto-fade timer for 10 seconds:`, delta.message);
                                    const autoFadeTimer = setTimeout(() => {
                                        console.log(`   ⏰ [${new Date().toISOString()}] Auto-fade timer triggered (10s elapsed):`, delta.message);
                                        setAnswerMetadata(prev => {
                                            const updated = new Map(prev);
                                            const current = updated.get(newAnswerIndex);
                                            if (current && !current.fadingOut) {
                                                const timeShown = Date.now() - current.timestamp;
                                                console.log(
                                                    `   🔄 [${new Date().toISOString()}] Auto-fade: Setting metadata to fadingOut=true (shown ${timeShown}ms):`,
                                                    current.message
                                                );
                                                updated.set(newAnswerIndex, { ...current, fadingOut: true });
                                                setTimeout(() => {
                                                    console.log(
                                                        `   🗑️  [${new Date().toISOString()}] Auto-fade: Removing metadata from state after fade:`,
                                                        current.message
                                                    );
                                                    setAnswerMetadata(prev => {
                                                        const updated = new Map(prev);
                                                        const current = updated.get(newAnswerIndex);
                                                        if (current) {
                                                            updated.set(newAnswerIndex, { ...current, fadingOut: false, hidden: true });
                                                        }
                                                        return updated;
                                                    });
                                                }, fadeOutDuration);
                                            }
                                            return updated;
                                        });
                                    }, 10000);
                                    metadataTimersRef.current.set(newAnswerIndex, autoFadeTimer);
                                }, fadeOutDuration);
                            }, waitTime);

                            return prev; // Keep existing visible during wait
                        }
                    });
                } else if (event["choices"] && event["choices"][0]["delta"]["content"]) {
                    const deltaContent = event["choices"][0]["delta"]["content"];
                    const isStatus = event["choices"][0]["delta"]["isStatus"] === true;

                    if (isStatus) {
                        // This is a status message - update status state with timing
                        // console.log(`📊 UI received status: "${deltaContent}"`);

                        // Mark that we have active status for this answer
                        setHasActiveStatus(prev => {
                            const updated = new Map(prev);
                            updated.set(newAnswerIndex, true);
                            return updated;
                        });

                        // Clear any existing timer for this answer index
                        const existingTimer = statusTimersRef.current.get(newAnswerIndex);
                        if (existingTimer) {
                            clearTimeout(existingTimer);
                        }

                        setStatusMessages(prev => {
                            const updated = new Map(prev);
                            const now = Date.now();
                            const existing = updated.get(newAnswerIndex);
                            const fadeOutDuration = 300; // 300ms fade out animation

                            // If there's an existing message, ensure it's been shown for at least 2 seconds
                            if (existing && !existing.fadingOut) {
                                const timeSinceLastUpdate = now - existing.timestamp;
                                const minDisplayTime = 2000; // 2 seconds

                                if (timeSinceLastUpdate < minDisplayTime) {
                                    // Schedule the update after the minimum display time + fade out
                                    const remainingTime = minDisplayTime - timeSinceLastUpdate;
                                    const timer = setTimeout(() => {
                                        // First fade out the old message
                                        setStatusMessages(prev => {
                                            const updated = new Map(prev);
                                            const existing = updated.get(newAnswerIndex);
                                            if (existing) {
                                                updated.set(newAnswerIndex, { ...existing, fadingOut: true });
                                            }
                                            return updated;
                                        });

                                        // Then fade in the new message after fade out completes
                                        setTimeout(() => {
                                            setStatusMessages(prev => {
                                                const updated = new Map(prev);
                                                updated.set(newAnswerIndex, { message: deltaContent, timestamp: Date.now() });
                                                return updated;
                                            });
                                            // Keep status active since we're showing a new status message
                                        }, fadeOutDuration);
                                    }, remainingTime);
                                    statusTimersRef.current.set(newAnswerIndex, timer);
                                    return prev; // Don't update yet
                                } else {
                                    // Enough time has passed, fade out old and fade in new
                                    updated.set(newAnswerIndex, { ...existing, fadingOut: true });
                                    setTimeout(() => {
                                        setStatusMessages(prev => {
                                            const updated = new Map(prev);
                                            updated.set(newAnswerIndex, { message: deltaContent, timestamp: Date.now() });
                                            return updated;
                                        });
                                        // Keep status active since we're showing a new status message
                                    }, fadeOutDuration);
                                    return updated;
                                }
                            }

                            // Update immediately if no existing message or it's already fading out
                            updated.set(newAnswerIndex, { message: deltaContent, timestamp: now });
                            return updated;
                        });
                    } else {
                        // This is regular content
                        // console.log(`🎨 UI received chunk: "${deltaContent.substring(0, 20)}"`);
                        answerContent += deltaContent;

                        // Track that the last message type was content, even if it's whitespace.
                        // Markdown streams often end with whitespace-only chunks.
                        lastMessageTypeRef.current.set(newAnswerIndex, "content");

                        // Note: Regular content does NOT affect metadata messages directly
                        // Metadata only fades out when:
                        // 1. A new metadata message arrives (after 1s minimum display)
                        // 2. End event is received AFTER content (after 1s minimum display)
                        // 3. Auto-fade after 10 seconds

                        // Check if we have active status messages - if so, buffer the content
                        const hasStatus = hasActiveStatus.get(newAnswerIndex);
                        if (hasStatus) {
                            // Buffer the content instead of displaying it
                            const currentBuffer = bufferedContentRef.current.get(newAnswerIndex) || "";
                            bufferedContentRef.current.set(newAnswerIndex, currentBuffer + deltaContent);
                        } else {
                            // No active status, display immediately
                            setAnswers(prevAnswers => {
                                const updated = [...prevAnswers];
                                const clonedResponse = { ...askResponse };
                                if (!clonedResponse.choices) {
                                    clonedResponse.choices = [
                                        {
                                            message: { content: "", role: "assistant" },
                                            context: { data_points: [], followup_questions: [], thoughts: [] },
                                            index: 0,
                                            session_state: null
                                        }
                                    ];
                                }
                                clonedResponse.choices[0] = {
                                    ...clonedResponse.choices[0],
                                    message: { content: answerContent, role: "assistant" },
                                    session_state: {
                                        ...clonedResponse.choices[0]?.session_state,
                                        ...responseSessionState
                                    }
                                };
                                updated[newAnswerIndex] = [question, clonedResponse];
                                return updated;
                            });
                        }
                    }
                } else if (event["choices"] && event["choices"][0]["context"]) {
                    askResponse.choices[0].context = {
                        ...askResponse.choices[0].context,
                        ...event["choices"][0]["context"]
                    };
                } else if (event["error"]) {
                    throw new Error(event["error"]);
                }
            }

            // Function to flush buffered content and clear status
            const flushBufferedContent = () => {
                const buffered = bufferedContentRef.current.get(newAnswerIndex) || "";
                if (buffered) {
                    // Flush the buffered content to the answer
                    setAnswers(prevAnswers => {
                        const updated = [...prevAnswers];
                        const clonedResponse = { ...askResponse };
                        if (!clonedResponse.choices) {
                            clonedResponse.choices = [
                                {
                                    message: { content: "", role: "assistant" },
                                    context: { data_points: [], followup_questions: [], thoughts: [] },
                                    index: 0,
                                    session_state: null
                                }
                            ];
                        }
                        clonedResponse.choices[0] = {
                            ...clonedResponse.choices[0],
                            message: { content: answerContent + buffered, role: "assistant" },
                            session_state: {
                                ...clonedResponse.choices[0]?.session_state,
                                ...responseSessionState
                            }
                        };
                        updated[newAnswerIndex] = [question, clonedResponse];
                        return updated;
                    });
                    bufferedContentRef.current.delete(newAnswerIndex);
                }

                // Clear active status flag
                setHasActiveStatus(prev => {
                    const updated = new Map(prev);
                    updated.delete(newAnswerIndex);
                    return updated;
                });
            };

            // Wait for minimum display time, then fade out and flush content
            setTimeout(() => {
                setStatusMessages(prev => {
                    const updated = new Map(prev);
                    const existing = updated.get(newAnswerIndex);
                    if (existing) {
                        const timeSinceLastUpdate = Date.now() - existing.timestamp;
                        const minDisplayTime = 2000; // 2 seconds
                        const fadeOutDuration = 300; // 300ms fade out animation

                        if (timeSinceLastUpdate >= minDisplayTime) {
                            // Mark as fading out, then remove after animation and flush content
                            updated.set(newAnswerIndex, { ...existing, fadingOut: true });
                            setTimeout(() => {
                                setStatusMessages(prev => {
                                    const updated = new Map(prev);
                                    updated.delete(newAnswerIndex);
                                    return updated;
                                });
                                flushBufferedContent();
                            }, fadeOutDuration);
                        } else {
                            // Schedule fade out after remaining time
                            const remainingTime = minDisplayTime - timeSinceLastUpdate;
                            setTimeout(() => {
                                setStatusMessages(prev => {
                                    const updated = new Map(prev);
                                    const existing = updated.get(newAnswerIndex);
                                    if (existing) {
                                        updated.set(newAnswerIndex, { ...existing, fadingOut: true });
                                        setTimeout(() => {
                                            setStatusMessages(prev => {
                                                const updated = new Map(prev);
                                                updated.delete(newAnswerIndex);
                                                return updated;
                                            });
                                            flushBufferedContent();
                                        }, fadeOutDuration);
                                    }
                                    return updated;
                                });
                            }, remainingTime);
                        }
                    } else {
                        // No status message, flush immediately
                        flushBufferedContent();
                    }
                    return updated;
                });
            }, 100); // Small delay to ensure smooth transition

            // ENG-027: the finished answer and the agent's closure verdict (end frame), if any.
            return abortSignal.aborted ? null : { content: answerContent, closure: askResponse.choices[0]?.context?.closure };
        } catch (e: any) {
            // Ignore abort errors
            if (e?.name === "AbortError" || abortSignal.aborted) {
                // Request aborted by user
                return null;
            } else {
                throw e;
            }
        } finally {
            const streamingEndTime = Date.now();
            const totalStreamingTime = streamingEndTime - streamingStartTime;
            const totalResponseTime = streamingEndTime - requestStartTime;
            console.log(
                `[${new Date().toISOString()}] Streaming completed - Total stream time: ${totalStreamingTime}ms, Total response time: ${totalResponseTime}ms`
            );
            setIsStreaming(false);
            // Clear status message when streaming completes (with fade out delay)
            const timer = statusTimersRef.current.get(newAnswerIndex);
            if (timer) {
                clearTimeout(timer);
            }
        }
    };

    const updateScrollPosition = () => {
        if (!chatScrollRef.current) return;
        const { scrollHeight, scrollTop, clientHeight } = chatScrollRef.current;
        const atBottom = scrollHeight - scrollTop - clientHeight < 2;
        setIsAtBottom(atBottom);
    };

    // ENG-027: after an answer finishes, arm the rating prompt if the answer closes the question.
    // The agent backend's verdict wins; n8n sends none, so the text is classified here.
    const armRatingPrompt = (sessionState: any, answerIndex: number, content: string | undefined, gatewayClosure: AnswerClosure | undefined) => {
        let closure: FeedbackClosure | null = null;
        if (gatewayClosure) closure = { ...gatewayClosure, source: "gateway" };
        else if (content !== undefined) closure = { ...classifyAnswerClosure(content), source: "frontend" };
        ratingPrompt.onAnswerComplete(sessionState?.n8n_session_id || "", answerIndex, closure);
    };

    const makeApiRequest = async (
        question: string,
        skipInsert = false,
        targetAnswerIndex?: number,
        sendMeta?: SendMeta,
        introSuggestion = false,
        introSuggestionId?: string
    ) => {
        // ENG-054: a new message goes to the thread of the product the host shows right now, so
        // re-read it first (an SPA pushState or a re-init fires no event). If that, or a host
        // switch earlier in this same tick, moves the thread, wait for that thread's history and
        // send it there (queuedRequestRef below).
        if (!skipInsert && targetAnswerIndex === undefined) {
            conversationThread.syncWithHost();
            if (conversationThread.current().threadKey !== historyThreadKey) {
                queuedRequestRef.current = { question, sendMeta, introSuggestion, introSuggestionId };
                return;
            }
        }
        const threadState = conversationThread.current();

        // Abort any existing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new AbortController for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        const requestStartTime = Date.now();
        console.log(`[${new Date().toISOString()}] Request start - Question: "${question}"`);
        lastQuestionRef.current = question;
        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        const conversationSnapshot = targetAnswerIndex !== undefined ? answers.slice(0, targetAnswerIndex) : [...answers];
        const originalTargetAnswer = targetAnswerIndex !== undefined ? answers[targetAnswerIndex] : undefined;
        const newAnswerIndex = targetAnswerIndex ?? conversationSnapshot.length;
        setAnswerWebCitations(prev => {
            const updated = new Map(prev);
            updated.delete(newAnswerIndex);
            return updated;
        });

        const rollbackFailedPlaceholder = () => {
            if (skipInsert) return;
            setAnswers(prev => {
                if (newAnswerIndex < 0 || newAnswerIndex >= prev.length) return prev;
                const candidate = prev[newAnswerIndex];
                const candidateContent = candidate?.[1]?.choices?.[0]?.message?.content;
                if (candidateContent !== processingMessage) return prev;

                // Retry path: restore the previous answer for this slot.
                if (targetAnswerIndex !== undefined && originalTargetAnswer) {
                    const updated = [...prev];
                    updated[newAnswerIndex] = originalTargetAnswer;
                    return updated;
                }

                // New question path: remove failed placeholder.
                const updated = [...prev];
                updated.splice(newAnswerIndex, 1);
                return updated;
            });
        };

        if (!skipInsert) {
            setAnswers(prev => {
                // console.log(`[${new Date().toISOString()}] Immediately inserting new answer placeholder for question: "${question}"`);
                const placeholder: [string, ChatAppResponse] = [
                    question,
                    {
                        choices: [
                            {
                                message: { content: processingMessage, role: "assistant" },
                                context: {},
                                index: 0,
                                session_state: null
                            }
                        ]
                    } as ChatAppResponse
                ];

                if (targetAnswerIndex !== undefined) {
                    const updated = [...prev];
                    updated[targetAnswerIndex] = placeholder;
                    return updated;
                }

                return [...prev, placeholder];
            });
        }

        const previousMessages: ResponseMessage[] = conversationSnapshot.flatMap(a => [
            { content: a[0], role: "user" },
            { content: a[1].choices[0].message.content, role: "assistant" }
        ]);

        // Widget user info for analytics (no-auth case)
        const widgetCfg = (window as any)?.PublicWidgetConfig;
        const widgetUserId = widgetCfg?.userId;
        const widgetUserName = widgetCfg?.userName;
        const widgetUserEmail = widgetCfg?.userEmail;

        // Build host context from the latest structured context (set via ChatWidget.init /
        // setHostContext), falling back to the browser URL for the web integration. Read at
        // send time so a preceding setHostContext() call is respected. The SKU precedence is:
        // hostContext.productSku -> browser URL -> none.
        const structuredHostCtx: any = (isWidget && (window as any)?.PublicWidgetConfig?.hostContext) || {};
        // ENG-054: the thread decides the product, its SKU in a product thread and none in the
        // general thread, which never sends productSku/productTitle (README "Κοινό συμβόλαιο").
        const hostProductSku = threadHostProductSku(threadState);
        const hostContextData: Record<string, string> = {};

        const source = normalizeHostContextField(structuredHostCtx.source);
        const screen = normalizeHostContextField(structuredHostCtx.screen);
        const storeId = normalizeHostContextField(structuredHostCtx.storeId);
        const storeName = normalizeHostContextField(structuredHostCtx.storeName);
        if (source) hostContextData.source = source;
        if (screen) hostContextData.screen = screen;
        if (storeId) hostContextData.storeId = storeId;
        if (storeName) hostContextData.storeName = storeName;

        if (hostProductSku) {
            // Prefer the host-provided title; only fetch it when absent. Naming the product
            // helps the agent tell products apart across a mid-conversation SKU change.
            // ENG-055: the host's title names the host's own product only; a thread picked from
            // "Recent" is another product, so its title comes from the registry (or the lookup).
            const hostTitleFitsThread = normalizeHostContextField(structuredHostCtx.productSku) === hostProductSku;
            let hostProductTitle =
                (hostTitleFitsThread ? normalizeHostContextField(structuredHostCtx.productTitle, 500) : undefined) || productThreadTitle(hostProductSku) || "";
            if (!hostProductTitle) {
                try {
                    const details = await getProductDetails(hostProductSku, siteId, ["content"]);
                    hostProductTitle = normalizeHostContextField(details?.content, 500) || "";
                } catch (e) {
                    console.debug("[chat] failed to fetch host product title", { hostProductSku, error: e });
                }
            }
            hostContextData.productSku = hostProductSku;
            if (hostProductTitle) hostContextData.productTitle = hostProductTitle;
        }

        // Keep host metadata structured all the way to the gateway. The gateway validates
        // the same allow-list again before placing it in n8n's delimited data section.
        const hostContext: string | undefined = Object.keys(hostContextData).length > 0 ? JSON.stringify(hostContextData) : undefined;
        const normalizedHostSource = String(hostContextData.source || "")
            .trim()
            .toLowerCase();
        const clientChannel = resolveClientChannel(isWidget, normalizedHostSource);
        console.debug("[chat] host context", {
            isWidget,
            hostUrl: window.location.href,
            hostProductSku,
            hostContext,
            clientChannel
        });

        const request: ChatAppRequest = {
            messages: [...previousMessages, { content: question, role: "user" }],
            stream: shouldStream,
            client_channel: clientChannel,
            context: {
                overrides: {
                    exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                    vector_fields: vectorFieldList,
                    top: retrieveCount,
                    retrieval_mode: retrievalMode,
                    semantic_ranker: useSemanticRanker,
                    intent_model: intentModel,
                    response_model: responseModel,
                    reasoning_effort: reasoningEffort,
                    price_source: priceSource,
                    site_id: siteId
                }
            },
            ...(widgetUserId ? { user_id: widgetUserId } : {}),
            ...(widgetUserName ? { user_name: widgetUserName } : {}),
            ...(widgetUserEmail ? { user_email: widgetUserEmail } : {}),
            ...(hostContext ? { host_context: hostContext } : {}),
            ...(sendMeta ? { input_mode: sendMeta.inputMode } : {}),
            ...(sendMeta?.inputMode === "voice" ? { voice_edited: sendMeta.voiceEdited, speech_backend: sendMeta.speechBackend } : {}),
            // ENG-014: only a voice message the auto-send timer sent carries auto_submitted: true.
            ...(sendMeta?.inputMode === "voice" && sendMeta.autoSubmitted ? { auto_submitted: true } : {}),
            // ENG-054: conversation_thread always, intro_suggestion only from an intro button.
            // ENG-067: and next to it the button's intro_suggestion_id ("<group>.<question>").
            ...threadRequestFields(threadState, introSuggestion, introSuggestionId),
            session_state: {
                ...(conversationSnapshot.at(-1)?.[1].choices[0].session_state || {}),
                // ENG-054: each thread has its own session.
                n8n_session_id: getN8NSessionId(siteId, undefined, threadState.threadKey)
            }
        };

        const token = client ? await getToken(client) : undefined;

        try {
            const response = await chatApi(request, token, abortController.signal);

            // New shot begun; clear previous selections so identical future questions start fresh
            clearAllSelections();

            const responseReceivedTime = Date.now();
            const timeToFirstResponse = responseReceivedTime - requestStartTime;
            console.log(`[${new Date().toISOString()}] Response received - Time to first response: ${timeToFirstResponse}ms`);

            // Apply streaming headers (if any) to immediately reflect enforced models
            try {
                const hdrIntent = response.headers.get("X-Intent-Model");
                const hdrResponse = response.headers.get("X-Response-Model");
                const hdrReasoning = response.headers.get("X-Reasoning-Effort");
                const hdrAllowOverride = response.headers.get("X-Allow-User-Override");
                if (hdrIntent) setIntentModel(hdrIntent);
                if (hdrResponse) setResponseModel(hdrResponse);
                if (hdrReasoning) setReasoningEffort(hdrReasoning);
                if (hdrAllowOverride !== null) {
                    setModelConfig(prev => (prev ? { ...prev, allowUserOverride: hdrAllowOverride === "true" } : prev));
                }
            } catch (e) {
                console.log("Could not apply model headers:", e);
            }

            const hdrSessionId = response.headers.get("X-Session-Id");
            const hdrTurnId = response.headers.get("X-Turn-Id");
            const responseSessionState = {
                ...request.session_state,
                ...(hdrSessionId ? { n8n_session_id: hdrSessionId } : {}),
                ...(hdrTurnId ? { turn_id: hdrTurnId } : {})
            };

            if (!response.body) {
                throw new Error("No response body");
            }

            try {
                if (shouldStream) {
                    const outcome = await handleAsyncRequest(
                        question,
                        response.body,
                        newAnswerIndex,
                        abortController.signal,
                        requestStartTime,
                        responseSessionState
                    );
                    armRatingPrompt(responseSessionState, newAnswerIndex, outcome?.content, outcome?.closure);
                } else {
                    const parsedResponse: ChatAppResponseOrError = await response.json();
                    console.log(`[${new Date().toISOString()}] Parsed API response:`, parsedResponse);
                    if (response.status > 299 || !response.ok) {
                        throw new Error(parsedResponse.error || "Unknown error");
                    }
                    // Apply effective model config from response (non-streaming)
                    try {
                        const anyResp: any = parsedResponse as any;
                        const applied = anyResp?.applied_models;
                        const eff = anyResp?.effective_model_config;
                        if (applied) {
                            if (applied.intent_model) setIntentModel(applied.intent_model);
                            if (applied.response_model) setResponseModel(applied.response_model);
                            if (applied.reasoning_effort) setReasoningEffort(applied.reasoning_effort);
                        }
                        if (eff) {
                            setModelConfig(prev => ({
                                enabled: eff.enabled,
                                allowUserOverride: eff.allowUserOverride,
                                availableModels: eff.availableModels || prev?.availableModels || [],
                                defaultIntentModel: eff.defaultIntentModel,
                                defaultResponseModel: eff.defaultResponseModel,
                                defaultReasoningEffort: eff.defaultReasoningEffort
                            }));
                        }
                    } catch (e) {
                        console.log("Could not apply effective model config from response:", e);
                    }
                    setAnswers(prev => {
                        console.log(`[${new Date().toISOString()}] Updating non-streaming answer for question: "${question}"`);
                        const updated = [...prev];
                        const nextResponse = parsedResponse as ChatAppResponse;
                        if (nextResponse.choices?.[0]) {
                            nextResponse.choices[0] = {
                                ...nextResponse.choices[0],
                                session_state: {
                                    ...nextResponse.choices[0].session_state,
                                    ...responseSessionState
                                }
                            };
                        }
                        updated[newAnswerIndex] = [question, nextResponse];
                        return updated;
                    });
                    const nonStreamChoice = (parsedResponse as ChatAppResponse).choices?.[0];
                    armRatingPrompt(responseSessionState, newAnswerIndex, nonStreamChoice?.message?.content, nonStreamChoice?.context?.closure);

                    const completionTime = Date.now();
                    const totalTime = completionTime - requestStartTime;
                    console.log(`[${new Date().toISOString()}] Non-streaming completed - Total time: ${totalTime}ms`);
                }
            } catch (e: any) {
                // Ignore abort errors
                if (e?.name === "AbortError" || abortController.signal.aborted) {
                    // Don't remove the answer if it's already been replaced with stopped message
                    // The stopRequest function handles replacing it, so we just skip removal here
                    if (!skipInsert) {
                        setAnswers(prev => {
                            const lastAnswer = prev.at(-1);
                            if (lastAnswer) {
                                const lastMessageContent = lastAnswer[1]?.choices?.[0]?.message?.content;
                                // Only remove if it's still the placeholder, not if it's already been replaced
                                if (lastMessageContent === processingMessage) {
                                    return prev.slice(0, -1);
                                }
                            }
                            return prev;
                        });
                    }
                } else {
                    rollbackFailedPlaceholder();
                    throw e;
                }
            }
        } catch (e: any) {
            // Ignore abort errors
            if (e?.name === "AbortError" || abortController.signal.aborted) {
                // Don't remove the answer if it's already been replaced with stopped message
                // The stopRequest function handles replacing it, so we just skip removal here
                if (!skipInsert) {
                    setAnswers(prev => {
                        const lastAnswer = prev.at(-1);
                        if (lastAnswer) {
                            const lastMessageContent = lastAnswer[1]?.choices?.[0]?.message?.content;
                            // Only remove if it's still the placeholder, not if it's already been replaced
                            if (lastMessageContent === processingMessage) {
                                return prev.slice(0, -1);
                            }
                        }
                        return prev;
                    });
                }
            } else {
                console.error(`[${new Date().toISOString()}] Error in makeApiRequest:`, e);
                rollbackFailedPlaceholder();
                setError(e);
            }
        } finally {
            setIsLoading(false);
            setIsStreaming(false);
            // Clear abort controller if this was the active request
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null;
            }
            console.debug(`[${new Date().toISOString()}] makeApiRequest completed for question: "${question}"`);
        }
    };

    const stopRequest = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            // Capture states before clearing them
            const wasStreaming = isStreaming;
            const wasLoading = isLoading;
            // Immediately clear loading states to hide the loading indicator
            setIsLoading(false);
            setIsStreaming(false);
            // Replace the last answer (whether placeholder or partial content) with stopped message
            // If abortController exists, there's an active request, so always try to replace the last answer
            setAnswers(prev => {
                const lastAnswer = prev.at(-1);
                if (lastAnswer) {
                    const lastMessageContent = lastAnswer[1]?.choices?.[0]?.message?.content;
                    // Always replace if it's the placeholder, or if there was active loading/streaming
                    // This ensures we catch it even if states aren't perfectly synchronized
                    const shouldReplace =
                        lastMessageContent === processingMessage ||
                        wasLoading ||
                        wasStreaming ||
                        (lastMessageContent && lastMessageContent.trim().length > 0 && lastMessageContent !== "Response stopped by user.");

                    if (shouldReplace) {
                        const updated = [...prev];
                        const lastIndex = updated.length - 1;
                        updated[lastIndex] = [
                            lastAnswer[0],
                            {
                                ...lastAnswer[1],
                                choices: [
                                    {
                                        ...lastAnswer[1].choices[0],
                                        message: {
                                            content: "Response stopped by user.",
                                            role: "assistant"
                                        },
                                        context: {
                                            ...lastAnswer[1].choices[0].context,
                                            data_points: [],
                                            followup_questions: [],
                                            thoughts: []
                                        }
                                    }
                                ]
                            }
                        ];
                        return updated;
                    }
                }
                return prev;
            });
            // Don't clear ref here - let the async cleanup handle it
        }
    };

    const clearChat = () => {
        // Starting a new conversation rotates the backend session so the next message opens
        // a fresh n8n conversation for the active user + site. Messages, citations and the
        // last question are cleared via the state resets below (which the persistence effect
        // then flushes to storage).
        // ENG-054: only the thread on screen; the other threads keep their history and session.
        try {
            clearN8NSession(siteId, undefined, conversationThread.threadKey);
        } catch {}
        conversationThread.clearNotice();
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        ratingPrompt.reset();
        setIsLoading(false);
        setIsStreaming(false);
        setShowExamples(false); // Reset examples visibility
        const metadataCount = answerMetadata.size;
        if (metadataCount > 0) {
            console.log(`   🧹 [${new Date().toISOString()}] clearChat: Clearing ${metadataCount} metadata message(s)`);
        }
        setAnswerMetadata(new Map()); // Clear metadata
        setAnswerWebCitations(new Map()); // Clear web research citations

        // Clear all metadata timers
        const timerCount = metadataTimersRef.current.size;
        if (timerCount > 0) {
            console.log(`   🧹 [${new Date().toISOString()}] clearChat: Clearing ${timerCount} metadata timer(s)`);
            metadataTimersRef.current.forEach(timer => clearTimeout(timer));
            metadataTimersRef.current.clear();
        }

        // Clear persisted question selections between shots
        clearAllSelections();

        // Reset scroll position to top with a small delay to ensure DOM updates first
        setTimeout(() => {
            if (chatScrollRef.current) {
                chatScrollRef.current.scrollTop = 0;
            }
        }, 50);
    };

    // ENG-054: send the queued message once its thread's history is on screen.
    useEffect(() => {
        const queued = queuedRequestRef.current;
        if (!queued || conversationThread.current().threadKey !== historyThreadKey) return;
        queuedRequestRef.current = null;
        makeApiRequest(queued.question, false, undefined, queued.sendMeta, queued.introSuggestion, queued.introSuggestionId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyThreadKey]);

    // ENG-054: what belongs to the thread just left (an error and its retry, an open citation
    // panel or print dialog, the examples list, answer status lines) must not show in the new one.
    useEffect(() => {
        setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setPendingPrintAnswerIndex(null);
        setShowExamples(false);
        metadataTimersRef.current.forEach(timer => clearTimeout(timer));
        metadataTimersRef.current.clear();
        setAnswerMetadata(new Map());
    }, [conversationThread.threadKey]);

    // ENG-054: an intro suggestion is an ordinary message, marked intro_suggestion: true.
    // ENG-067: plus which ready question it was (intro_suggestion_id).
    const onIntroSuggestion = (question: string, suggestionId: string) => {
        scrollOnUserSubmitRef.current = true;
        ratingPrompt.onUserActivity();
        makeApiRequest(question, false, undefined, undefined, true, suggestionId);
    };
    const showProductIntro = conversationThread.thread === "product" && answers.length === 0 && !isLoading && historyThreadKey === conversationThread.threadKey;

    useEffect(() => {
        // Skip config loading for widget users since they use token-based auth
        if (!client && !isWidget) {
            // Check if client is available
            getConfig();
        }
    }, [client, isWidget]); // Add dependencies

    useEffect(() => {
        // Fetch model configuration
        const fetchModelConfig = async () => {
            try {
                const baseUrl = getBackendUri();
                const response = await fetch(`${baseUrl}/model-config`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json"
                    }
                });

                if (response.ok) {
                    const config = await response.json();
                    setModelConfig(config);

                    // Update model selections with defaults from config if not already set
                    if (!initialModels?.intentModel) {
                        setIntentModel(config.defaultIntentModel);
                    }
                    if (!initialModels?.responseModel) {
                        setResponseModel(config.defaultResponseModel);
                    }
                    if (!initialModels?.reasoningEffort) {
                        setReasoningEffort(config.defaultReasoningEffort);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch model configuration:", error);
            }
        };

        fetchModelConfig();
    }, []);

    useEffect(() => {
        const fetchN8nConfig = async () => {
            try {
                const baseUrl = getBackendUri();
                const response = await fetch(`${baseUrl}/n8n-config`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json"
                    }
                });
                if (response.ok) {
                    const config = await response.json();
                    setN8nConfig(config);
                }
            } catch (error) {
                console.error("Failed to fetch n8n configuration:", error);
            } finally {
                setN8nConfigSettled(true);
            }
        };

        fetchN8nConfig();
    }, []);

    // ENG-015: admin voice kill switch for this client's channel (global && per channel).
    const adminVoiceEnabled = n8nConfigSettled && isVoiceEnabledForChannel(n8nConfig?.speech, resolveClientChannel(isWidget, currentHostSource()));
    // ENG-034: the widget's `speechEnabled: false` init option hides the mic outright, ahead of
    // (and independent from) the admin switch — for hosts supplying their own speech-to-text
    // (e.g. Genie's native STT via ChatWidget.submitText()). The standalone app never sets
    // PublicWidgetConfig.speechEnabled, so it is unaffected. This does NOT gate voiceAvailable
    // below: the profile's auto-send toggle stays visible with no mic, because
    // submitText({inputMode:"voice"}) still uses that preference.
    const hostSpeechEnabled = !isWidget || (window as any)?.PublicWidgetConfig?.speechEnabled !== false;
    const voiceEnabled = hostSpeechEnabled && adminVoiceEnabled;
    // ENG-069: how long recognized text waits (countdown + Edit) before auto-send. QuestionInput
    // falls back to 3000 and clamps 1000-8000 when this is missing or not a number.
    const autoSendDelayMs = n8nConfig?.speech?.timeouts?.autoSendDelayMs?.value;
    const { setAvailable: setVoiceAvailable } = useVoiceAutoSend();
    useEffect(() => {
        if (n8nConfigSettled) {
            setVoiceAvailable(adminVoiceEnabled);
        }
    }, [n8nConfigSettled, adminVoiceEnabled, setVoiceAvailable]);

    useEffect(() => {
        if (!n8nConfig) return;
        const siteConfig = n8nConfig.regions?.[siteId] || { enabled: false, chatUrl: "" };
        const chatUrl = (siteConfig.chatUrl || "").trim();
        const enabled = !!siteConfig.enabled && !!chatUrl;

        if (enabled) {
            (globalThis as any).N8N_CHAT_URL = chatUrl;
        } else {
            try {
                delete (globalThis as any).N8N_CHAT_URL;
            } catch {}
        }
        localStorage.removeItem("N8N_CHAT_URL");
    }, [n8nConfig, siteId]);

    useEffect(() => {
        if (!chatScrollRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            updateScrollPosition();
        });

        resizeObserver.observe(chatScrollRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (scrollOnUserSubmitRef.current) {
            scrollOnUserSubmitRef.current = false;
            const timeout = setTimeout(() => {
                scrollToBottom();
            }, 100);
            return () => clearTimeout(timeout);
        }
    }, [answers]);

    useEffect(() => {
        if (isStreaming) {
            const interval = setInterval(() => {
                updateScrollPosition();
            }, 250);

            return () => clearInterval(interval);
        } else {
            updateScrollPosition();
        }
    }, [isStreaming]);

    // Input changes handlers
    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };
    const onTemperatureChange = (newValue: number) => {
        setTemperature(newValue);
    };
    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "3"));
    };
    const onUseSemanticRankerChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticRanker(!!checked);
    };
    const onUseSemanticCaptionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticCaptions(!!checked);
    };
    const onShouldStreamChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setShouldStream(!!checked);
    };
    const onExcludeCategoryChanged = (_ev?: React.FormEvent, newValue?: string) => {
        setExcludeCategory(newValue || "");
    };
    const onUseSuggestFollowupQuestionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSuggestFollowupQuestions(!!checked);
    };
    const onUseOidSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseOidSecurityFilter(!!checked);
    };
    const onUseGroupsSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseGroupsSecurityFilter(!!checked);
    };
    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
        setShowExamples(false);
        setSelectedCategory(null);
        // Ensure scroll happens after the question is processed
        scrollOnUserSubmitRef.current = true;
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }
        setSelectedAnswer(index);
    };

    const handleRatingPromptRate = async (label: RatingLabel, details?: RatingDetails, feedbackId?: string) => {
        const target = ratingPrompt.prompt;
        const widgetCfg = (window as any)?.PublicWidgetConfig;
        const widgetUserId = widgetCfg?.userId;
        const widgetUserName = widgetCfg?.userName;
        const widgetUserEmail = widgetCfg?.userEmail;
        const user = client
            ? await getCurrentUser(client)
            : widgetUserId || widgetUserName || widgetUserEmail
            ? { id: widgetUserId, name: widgetUserName, email: widgetUserEmail }
            : undefined;

        const feedbackData = prepareFeedbackData(
            RATING_LABEL_VALUES[label],
            details?.comment ?? "",
            answers,
            sessionId,
            user,
            {
                intentModel,
                responseModel,
                reasoningEffort
            },
            feedbackId,
            {
                rating_scale: 3,
                rating_label: label,
                ...(details ? { reason_tags: details.reasonTags } : {}),
                ...(target ? { prompt_trigger: target.trigger, answer_index: target.answerIndex, closure: target.closure } : {})
            }
        );

        const token = client ? await getToken(client) : undefined;
        return submitFeedback(feedbackData, token);
    };

    const extractCopyText = (node: HTMLElement): string => {
        const clone = node.cloneNode(true) as HTMLElement;
        clone.querySelectorAll<HTMLElement>('[data-copy-skip="true"]').forEach(el => el.remove());
        clone.querySelectorAll<HTMLElement>('[data-copy-product="true"]').forEach(el => {
            el.insertAdjacentText("beforebegin", "\n— — —\n");
            el.insertAdjacentText("afterend", "\n— — —\n");
        });
        // innerText needs the node attached to the document to honor layout-based breaks.
        // Do NOT use visibility:hidden / display:none — innerText returns empty for those.
        clone.style.position = "fixed";
        clone.style.left = "-9999px";
        clone.style.top = "0";
        clone.style.pointerEvents = "none";
        clone.setAttribute("aria-hidden", "true");
        document.body.appendChild(clone);
        const text = clone.innerText;
        clone.remove();
        // Collapse duplicate separators (between back-to-back product cards).
        return text.replace(/(?:^|\n)— — —(?:\s*\n— — —)+/g, "\n— — —");
    };

    const copyAnswerMessage = async (answerIndex: number) => {
        const selectedAnswerPair = answers[answerIndex];
        if (!selectedAnswerPair) return;

        const [question, response] = selectedAnswerPair;
        const messageId = computeMessageId(answerIndex, question);
        const nodes = document.querySelectorAll<HTMLElement>(`[data-message-id="${messageId}"] [data-answer-content="true"]`);
        const rendered = Array.from(nodes)
            .map(n => extractCopyText(n).trim())
            .filter(Boolean)
            .join("\n\n");
        const assistantText = rendered || response?.choices?.[0]?.message?.content || "";

        await writeClipboardText(assistantText);
        setCopiedAnswerIndex(answerIndex);
        globalThis.setTimeout(() => {
            setCopiedAnswerIndex(current => (current === answerIndex ? null : current));
        }, 1800);
    };

    const getConversationExportId = (answerIndex: number): string => {
        const sessionState = answers[answerIndex]?.[1]?.choices?.[0]?.session_state;
        return sessionState?.n8n_session_id || sessionState?.conversation_id || sessionState?.conversationId || sessionId;
    };

    const toSafeFilenamePart = (value: string): string => {
        return value
            .trim()
            .replace(/[^a-zA-Z0-9._-]+/g, "-")
            .replace(/-+/g, "-")
            .replace(/^[-.]+|[-.]+$/g, "")
            .slice(0, 120);
    };

    const openPrintOptions = (answerIndex: number) => {
        setPendingPrintAnswerIndex(answerIndex);
    };

    const closePrintOptions = () => {
        setPendingPrintAnswerIndex(null);
    };

    const printPendingAnswer = (scope: PrintExportScope) => {
        const answerIndex = pendingPrintAnswerIndex;
        setPendingPrintAnswerIndex(null);
        if (answerIndex === null) return;
        globalThis.setTimeout(() => exportPdfThroughAnswer(answerIndex, scope), 250);
    };

    const exportPdfThroughAnswer = (answerIndex: number, scope: PrintExportScope) => {
        const transientHiddenNodes = new Set<HTMLElement>();
        const printRoot = chatRootRef.current;
        const printQueryRoot: ParentNode = printRoot ?? document;
        const setTransientPrintHidden = (node: HTMLElement) => {
            node.dataset.print = "hide";
            transientHiddenNodes.add(node);
        };

        const allTurnNodes = printQueryRoot.querySelectorAll<HTMLElement>("[data-print-turn-index]");
        allTurnNodes.forEach(node => {
            const turnIdx = Number(node.dataset.printTurnIndex);
            const shouldHideTurn = scope === "conversation" ? turnIdx > answerIndex : turnIdx !== answerIndex;
            if (shouldHideTurn) {
                setTransientPrintHidden(node);
            } else if (node.dataset.print === "hide") {
                delete node.dataset.print;
            }
        });

        if (scope === "message") {
            const selectedTurn = printQueryRoot.querySelector<HTMLElement>(`[data-print-turn-index="${answerIndex}"]`);
            const selectedUserMessage = selectedTurn?.querySelector<HTMLElement>('[data-print-role="user"]');
            if (selectedUserMessage) setTransientPrintHidden(selectedUserMessage);
        }

        document.getElementById("chat-widget-print-root")?.remove();
        const widgetPrintRoot = isWidget && printRoot ? document.createElement("div") : null;
        if (widgetPrintRoot && printRoot) {
            widgetPrintRoot.id = "chat-widget-print-root";
            widgetPrintRoot.appendChild(printRoot.cloneNode(true));
            document.body.appendChild(widgetPrintRoot);
        }

        const previousTitle = document.title;
        const conversationId = toSafeFilenamePart(getConversationExportId(answerIndex));
        document.title = `${scope === "conversation" ? "conversation" : "message"}-${conversationId}`;
        document.body.classList.add("is-printing");
        if (isWidget) document.body.classList.add("is-widget-printing");
        const cleanup = () => {
            document.title = previousTitle;
            document.body.classList.remove("is-printing");
            document.body.classList.remove("is-widget-printing");
            widgetPrintRoot?.remove();
            transientHiddenNodes.forEach(node => {
                if (node.dataset.print === "hide") delete node.dataset.print;
            });
            globalThis.removeEventListener("afterprint", cleanup);
        };
        globalThis.addEventListener("afterprint", cleanup);
        globalThis.print();
    };

    // Add widget-specific class to container
    return (
        <div className={`${styles.container} ${isWidget ? styles.widgetMode : ""}`}>
            <div ref={chatRootRef} className={`${styles.chatRoot} chat-page-root`}>
                <div className={styles.chatContainer}>
                    <ProductThreadBar
                        hostSku={conversationThread.hostSku}
                        pinnedSku={conversationThread.pinnedSku}
                        thread={conversationThread.thread}
                        notice={conversationThread.notice}
                        recent={recentThreads}
                        generalLastQuestion={generalLastQuestion}
                        knownTitle={productThreadTitle}
                        siteId={siteId}
                        exampleLanguage={exampleLanguage}
                        disabled={isLoading || isStreaming}
                        onChooseGeneral={conversationThread.chooseGeneral}
                        onChooseProduct={conversationThread.chooseProduct}
                        onChooseHost={conversationThread.chooseHost}
                        onChooseRecent={conversationThread.chooseRecent}
                        onTitle={rememberProductTitle}
                    />
                    {isEmptyState ? (
                        // Empty state - check if examples are shown
                        <div className={styles.chatMessageStream} ref={chatScrollRef} onScroll={handleScroll}>
                            {showProductIntro && conversationThread.productSku ? (
                                <ProductThreadIntro
                                    sku={conversationThread.productSku}
                                    siteId={siteId}
                                    priceSource={priceSource}
                                    exampleLanguage={exampleLanguage}
                                    disabled={isLoading || isStreaming}
                                    readyQuestions={n8nConfigSettled ? n8nConfig?.readyQuestions ?? null : undefined}
                                    onSuggestion={onIntroSuggestion}
                                    onTitle={rememberProductTitle}
                                />
                            ) : !showExamples ? (
                                // Center everything when no examples
                                <div className={styles.emptyStateWrapper}>
                                    <div className={styles.chatEmptyState}>
                                        <h1 className={styles.chatEmptyStateTitle}>
                                            {branding?.logo ? (
                                                <img
                                                    src={branding.logo}
                                                    alt="Logo"
                                                    style={{
                                                        width: isWidget ? "24px" : "36px",
                                                        height: isWidget ? "24px" : "36px",
                                                        borderRadius: "4px",
                                                        objectFit: "contain",
                                                        marginRight: "8px"
                                                    }}
                                                />
                                            ) : (
                                                <PublicIcon size={isWidget ? 24 : 36} />
                                            )}
                                            {resolvedAppTitle}
                                            <span className={styles.previewBadge}>Preview</span>
                                        </h1>

                                        <div className={styles.exampleToolbar}>
                                            <button className={styles.viewExamplesButton} onClick={() => setShowExamples(!showExamples)}>
                                                {showExamples
                                                    ? getLocalizedText(EXAMPLE_UI_COPY.hideExamples, exampleLanguage)
                                                    : getLocalizedText(EXAMPLE_UI_COPY.viewExamples, exampleLanguage)}
                                                <span style={{ transform: showExamples ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                                                    ▼
                                                </span>
                                            </button>
                                        </div>

                                        {/* Centered input for empty state without examples (disabled to always use bottom input) */}
                                        {false && (
                                            <div className={styles.chatInputCentered}>
                                                <div className={styles.chatInputWrapper}>
                                                    <QuestionInput
                                                        clearOnSend
                                                        placeholder={getLocalizedText(EXAMPLE_UI_COPY.inputPlaceholder, exampleLanguage)}
                                                        disabled={isLoading}
                                                        onSend={(question, meta) => {
                                                            scrollOnUserSubmitRef.current = true;
                                                            makeApiRequest(question, false, undefined, meta);
                                                        }}
                                                        onNewConversation={clearChat}
                                                        conversationThreadKey={conversationThread.threadKey}
                                                        // Widget prop
                                                        isWidget={isWidget}
                                                        priceSource={priceSource}
                                                        onPriceSourceChange={source => {
                                                            if (priceSourceLocked) return;
                                                            setPriceSource(source);
                                                        }}
                                                        priceSourceLocked={priceSourceLocked}
                                                        siteId={siteId}
                                                        onSiteIdChange={requestSiteIdChange}
                                                        siteIdLocked={siteIdLocked}
                                                        siteIdHasHistory={answers.length > 0}
                                                        exampleLanguage={exampleLanguage}
                                                        speechEnabled={voiceEnabled}
                                                        autoSendDelayMs={autoSendDelayMs}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                // Show examples at top of scrollable area
                                <div className={styles.chatContentWrapper}>
                                    <div className={styles.chatEmptyStateWithExamples}>
                                        <h1 className={styles.chatEmptyStateTitle}>
                                            {branding?.logo ? (
                                                <img
                                                    src={branding.logo}
                                                    alt="Logo"
                                                    style={{
                                                        width: isWidget ? "24px" : "36px",
                                                        height: isWidget ? "24px" : "36px",
                                                        borderRadius: "4px",
                                                        objectFit: "contain",
                                                        marginRight: "8px"
                                                    }}
                                                />
                                            ) : (
                                                <PublicIcon size={isWidget ? 24 : 36} />
                                            )}
                                            {resolvedAppTitle}
                                            <span className={styles.previewBadge}>Preview</span>
                                        </h1>

                                        <div className={styles.exampleToolbar}>
                                            <button className={styles.viewExamplesButton} onClick={() => setShowExamples(!showExamples)}>
                                                {showExamples
                                                    ? getLocalizedText(EXAMPLE_UI_COPY.hideExamples, exampleLanguage)
                                                    : getLocalizedText(EXAMPLE_UI_COPY.viewExamples, exampleLanguage)}
                                                <span style={{ transform: showExamples ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                                                    ▼
                                                </span>
                                            </button>
                                        </div>

                                        <div className={styles.exampleCategories}>
                                            <div className={styles.examplesIntro}>
                                                <div className={styles.examplesIntroTitle}>
                                                    {getLocalizedText(EXAMPLE_UI_COPY.panelSubtitle, exampleLanguage)}
                                                </div>
                                            </div>
                                            <div className={styles.categoryChipGrid}>
                                                {EXAMPLE_CATEGORIES.map(cat => (
                                                    <button
                                                        key={cat.id}
                                                        className={`${styles.categoryChip} ${selectedCategory === cat.id ? styles.categoryChipActive : ""}`}
                                                        onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                                                    >
                                                        {getLocalizedText(cat.shortTitle, exampleLanguage)}
                                                    </button>
                                                ))}
                                            </div>
                                            {selectedCategory &&
                                                (() => {
                                                    const cat = EXAMPLE_CATEGORIES.find(c => c.id === selectedCategory);
                                                    if (!cat) return null;
                                                    return (
                                                        <div className={styles.categoryPanel} key={cat.id}>
                                                            <div className={styles.categoryPanelHeader}>
                                                                <div className={styles.categoryPanelTitle}>{getLocalizedText(cat.title, exampleLanguage)}</div>
                                                                <button
                                                                    className={styles.categoryPanelClose}
                                                                    onClick={() => setSelectedCategory(null)}
                                                                    aria-label="Close"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                            <div className={styles.categoryDescription}>
                                                                {getLocalizedText(cat.description, exampleLanguage)}
                                                            </div>
                                                            {cat.disclaimer && (
                                                                <div className={styles.categoryDisclaimer}>
                                                                    {getLocalizedText(cat.disclaimer, exampleLanguage)}
                                                                </div>
                                                            )}
                                                            <div className={styles.examplesLabel}>{exampleLanguage === "el" ? "Παραδείγματα" : "Examples"}</div>
                                                            <div className={styles.exampleButtons}>
                                                                {cat.examples.map(example => (
                                                                    <button
                                                                        key={`${cat.id}-${getLocalizedText(example, exampleLanguage)}`}
                                                                        onClick={() => onExampleClicked(getLocalizedText(example, exampleLanguage))}
                                                                        className={styles.exampleButton}
                                                                    >
                                                                        {getLocalizedText(example, exampleLanguage)}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            {selectedCategory !== "orders" && (
                                                <div className={styles.excludedNotice}>
                                                    <span className={styles.excludedDescription}>{EXAMPLE_UI_COPY.excludedDescription[exampleLanguage]}</span>
                                                    <span className={styles.excludedDescription}>{EXAMPLE_UI_COPY.excludedDescription2[exampleLanguage]}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        // Chat conversation view
                        <div className={styles.chatMessageStream} ref={chatScrollRef} onScroll={handleScroll}>
                            <div
                                className={styles.chatContentWrapper}
                                style={{
                                    // Calculate padding based on number of answer lines
                                    // Widget uses smaller base padding (80px vs 140px)
                                    // Preview header: 60px, Each answer line: ~25px
                                    // The rating prompt (ENG-027) sits above the fixed input outside the widget.
                                    paddingBottom: `${
                                        (questionAnswers ? (isWidget ? 80 : 140) + 60 + questionAnswers.split("\n").length * 25 : isWidget ? 80 : 140) +
                                        (ratingPrompt.prompt && !isWidget ? RATING_PROMPT_RESERVED_PX : 0)
                                    }px`
                                }}
                            >
                                {answers.map((answerPair, index) => {
                                    const [question, chatResponse] = answerPair;
                                    const messageId = computeMessageId(index, question);
                                    const statusData = statusMessages.get(index);
                                    const statusMessage = statusData?.message;
                                    const isFadingOut = statusData?.fadingOut;
                                    const metadata = answerMetadata.get(index);
                                    const webCitations = answerWebCitations.get(index);
                                    const isInlineError = shouldRenderInlineError && index === answers.length - 1;
                                    return (
                                        <div key={index} data-print-turn-index={index}>
                                            <div data-print-role="user">
                                                <UserChatMessage message={question} />
                                            </div>
                                            {statusMessage && (
                                                <div
                                                    className={`${styles.statusMessage} ${isFadingOut ? styles.fadeOut : ""}`}
                                                    key={`${index}-${statusData.timestamp}`}
                                                    data-print="hide"
                                                >
                                                    <div className={styles.statusIndicator}>
                                                        <span className={styles.statusDot}></span>
                                                    </div>
                                                    <span className={styles.statusText}>{statusMessage}</span>
                                                </div>
                                            )}
                                            <div className={styles.chatMessageGpt} data-message-id={messageId}>
                                                <div className={styles.assistantMessageBody}>
                                                    <div className={styles.metadataSlot} data-print="hide">
                                                        {metadata && metadata.message && (
                                                            <div
                                                                className={`${styles.metadataInline} ${metadata.fadingOut ? styles.fadeOut : ""} ${
                                                                    metadata.hidden ? styles.hidden : ""
                                                                }`}
                                                                key={`metadata-${index}-${metadata.timestamp}`}
                                                            >
                                                                <span className={styles.metadataTextInline}>
                                                                    {metadata.message}
                                                                    <span className={styles.workingDotsInline}>
                                                                        <span>.</span>
                                                                        <span>.</span>
                                                                        <span>.</span>
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {isInlineError ? (
                                                        <AnswerError error={error?.toString() || ""} onRetry={() => makeApiRequest(question, false, index)} />
                                                    ) : (
                                                        <Answer
                                                            isStreaming={isStreaming && index === answers.length - 1}
                                                            answer={chatResponse}
                                                            isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                            onCitationClicked={c => onShowCitation(c, index)}
                                                            onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                            onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                            onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                            answerIndex={index}
                                                            branding={branding}
                                                            hasActiveMetadata={!!(metadata && !metadata.fadingOut && !metadata.hidden)}
                                                            webCitations={webCitations}
                                                            onCopyConversation={() => copyAnswerMessage(index)}
                                                            isConversationCopied={copiedAnswerIndex === index}
                                                            copyConversationLabel={getLocalizedText(CHAT_ACTION_COPY.copyConversation, exampleLanguage)}
                                                            copiedConversationLabel={getLocalizedText(CHAT_ACTION_COPY.copiedConversation, exampleLanguage)}
                                                            stoppedMessageLabel={getLocalizedText(CHAT_ACTION_COPY.stoppedResponse, exampleLanguage)}
                                                            onExportPdf={() => openPrintOptions(index)}
                                                            exportPdfLabel={getLocalizedText(CHAT_ACTION_COPY.printExport, exampleLanguage)}
                                                            onAnswersUpdate={
                                                                index === answers.length - 1
                                                                    ? (formattedAnswer: string) => {
                                                                          setQuestionAnswers(formattedAnswer);
                                                                      }
                                                                    : undefined
                                                            }
                                                            clearTriggerId={index === answers.length - 1 ? clearTriggerId : undefined}
                                                            priceSource={priceSource}
                                                            siteId={siteId}
                                                            onCategorySubmit={q => {
                                                                console.log(
                                                                    `[${new Date().toISOString()}] Category submission triggered with question: "${q}"`
                                                                );
                                                                scrollOnUserSubmitRef.current = true;

                                                                let enhancedQuestion = q;
                                                                try {
                                                                    const thoughts = (answers[index][1]?.choices?.[0]?.context as any)?.thoughts;

                                                                    if (thoughts && Array.isArray(thoughts)) {
                                                                        for (const thought of thoughts) {
                                                                            if (
                                                                                thought &&
                                                                                typeof thought === "object" &&
                                                                                thought.props &&
                                                                                thought.props.search_category
                                                                            ) {
                                                                                const searchCategory = thought.props.search_category;
                                                                                enhancedQuestion = `Κατηγορία: '${searchCategory}'\n${q}`;
                                                                                console.log(
                                                                                    `[${new Date().toISOString()}] Enhanced question with category: "${enhancedQuestion}"`
                                                                                );
                                                                                break;
                                                                            }
                                                                        }
                                                                    }
                                                                } catch (e) {
                                                                    console.log("Could not extract search_category:", e);
                                                                }

                                                                lastQuestionRef.current = enhancedQuestion;

                                                                setAnswers(prev => [
                                                                    ...prev,
                                                                    [
                                                                        enhancedQuestion,
                                                                        {
                                                                            choices: [
                                                                                {
                                                                                    message: { content: processingMessage, role: "assistant" },
                                                                                    context: {},
                                                                                    index: 0,
                                                                                    session_state: null
                                                                                }
                                                                            ]
                                                                        } as ChatAppResponse
                                                                    ]
                                                                ]);

                                                                makeApiRequest(enhancedQuestion, true);
                                                            }}
                                                            showFollowupQuestions={useSuggestFollowupQuestions && index === answers.length - 1}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {isLoading && !answers.length && (
                                    <>
                                        <UserChatMessage message={lastQuestionRef.current} />
                                        <div className={styles.chatMessageGpt}>
                                            <AnswerLoading />
                                        </div>
                                    </>
                                )}

                                {error && !shouldRenderInlineError ? (
                                    <>
                                        <div className={styles.chatMessageGpt}>
                                            <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    )}

                    {/* Input positioning based on state - only show if not widget in empty state, or always in widget mode */}
                    {true ? (
                        <div className={`${styles.chatInputBottom} ${isWidget ? styles.widgetInput : ""}`} data-print="hide">
                            {!isAtBottom && !isEmptyState && (
                                <button className={styles.scrollToBottomBtn} onClick={scrollToBottom}>
                                    ↓
                                </button>
                            )}
                            <div className={styles.chatInputWrapper}>
                                {ratingPrompt.prompt && (
                                    <RatingPrompt
                                        key={`${ratingPrompt.prompt.sessionKey}:${ratingPrompt.prompt.answerIndex}`}
                                        onRate={handleRatingPromptRate}
                                        onAnswered={ratingPrompt.markAsked}
                                        onClose={ratingPrompt.close}
                                        onNotify={(message, type) => setFeedbackToast({ message, type })}
                                        isWidget={isWidget}
                                    />
                                )}
                                <QuestionInput
                                    clearOnSend
                                    placeholder={
                                        isEmptyState
                                            ? getLocalizedText(EXAMPLE_UI_COPY.inputPlaceholder, exampleLanguage)
                                            : getLocalizedText(EXAMPLE_UI_COPY.replyPlaceholder, exampleLanguage)
                                    }
                                    disabled={isLoading}
                                    isResponseInProgress={isLoading || isStreaming}
                                    onStop={stopRequest}
                                    onSend={(question, meta) => {
                                        scrollOnUserSubmitRef.current = true;
                                        ratingPrompt.onUserActivity();
                                        // Combine user message with question answers if they exist
                                        const finalMessage = questionAnswers ? questionAnswers + (question ? "\n\n" + question : "") : question;
                                        makeApiRequest(finalMessage, false, undefined, meta);
                                        // Clear question answers and selections after sending
                                        setQuestionAnswers("");
                                        setClearTriggerId(prev => prev + 1);
                                        clearAllSelections();
                                    }}
                                    questionAnswers={questionAnswers}
                                    onClearQuestionAnswers={() => {
                                        setQuestionAnswers("");
                                        setClearTriggerId(prev => prev + 1);
                                        clearAllSelections();
                                    }}
                                    onNewConversation={clearChat}
                                    conversationThreadKey={conversationThread.threadKey}
                                    // Widget prop
                                    isWidget={isWidget}
                                    priceSource={priceSource}
                                    onPriceSourceChange={source => {
                                        if (priceSourceLocked) return;
                                        setPriceSource(source);
                                    }}
                                    priceSourceLocked={priceSourceLocked}
                                    siteId={siteId}
                                    onSiteIdChange={requestSiteIdChange}
                                    siteIdLocked={siteIdLocked}
                                    siteIdHasHistory={answers.length > 0}
                                    exampleLanguage={exampleLanguage}
                                    speechEnabled={voiceEnabled}
                                    autoSendDelayMs={autoSendDelayMs}
                                    onUserInput={ratingPrompt.onUserActivity}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>

                <Modal isOpen={showSiteIdConfirm} onDismiss={cancelSiteIdChange} isBlocking={true} containerClassName={styles.siteIdConfirmModal}>
                    <div className={styles.siteIdConfirmHeader}>
                        <Text className={styles.siteIdConfirmTitle}>Switch to {pendingSiteId || ""}?</Text>
                        <Text className={styles.siteIdConfirmSubtitle}>You have chat history in this site. Switching will load a different history.</Text>
                    </div>
                    <div className={styles.siteIdConfirmBody}>
                        <div className={styles.siteIdConfirmTransition}>
                            <span className={styles.siteIdPill}>{SITE_ID_LABELS[siteId]}</span>
                            <span className={styles.siteIdArrow}>&rarr;</span>
                            <span className={styles.siteIdPillAccent}>{pendingSiteId ? SITE_ID_LABELS[pendingSiteId] : ""}</span>
                        </div>
                    </div>
                    <div className={styles.siteIdConfirmFooter}>
                        <DefaultButton text="Cancel" onClick={cancelSiteIdChange} />
                        <PrimaryButton text="OK" onClick={confirmSiteIdChange} />
                    </div>
                </Modal>

                <Modal
                    isOpen={pendingPrintAnswerIndex !== null}
                    onDismiss={closePrintOptions}
                    isBlocking={false}
                    containerClassName={styles.siteIdConfirmModal}
                >
                    <div className={styles.siteIdConfirmHeader}>
                        <Text className={styles.siteIdConfirmTitle}>{getLocalizedText(CHAT_ACTION_COPY.printExport, exampleLanguage)}</Text>
                        <Text className={styles.siteIdConfirmSubtitle}>{getLocalizedText(CHAT_ACTION_COPY.printExportSubtitle, exampleLanguage)}</Text>
                    </div>
                    <div className={styles.siteIdConfirmFooter}>
                        <DefaultButton text={getLocalizedText(CHAT_ACTION_COPY.cancel, exampleLanguage)} onClick={closePrintOptions} />
                        <DefaultButton
                            text={getLocalizedText(CHAT_ACTION_COPY.printSelectedMessage, exampleLanguage)}
                            onClick={() => printPendingAnswer("message")}
                        />
                        <PrimaryButton
                            text={getLocalizedText(CHAT_ACTION_COPY.printConversation, exampleLanguage)}
                            onClick={() => printPendingAnswer("conversation")}
                        />
                    </div>
                </Modal>

                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisOverlay
                        isOpen={true}
                        onDismiss={() => {
                            setActiveAnalysisPanelTab(undefined);
                            setActiveCitation(undefined);
                        }}
                        answer={answers[selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                        activeCitation={activeCitation}
                        citationHeight="810px"
                        onActiveTabChanged={tab => onToggleTab(tab, selectedAnswer)}
                        priceSource={priceSource}
                    />
                )}

                {!isWidget && (
                    <Panel
                        headerText="Configure answer generation"
                        isOpen={isConfigPanelOpen}
                        isBlocking={false}
                        onDismiss={() => setIsConfigPanelOpen(false)}
                        closeButtonAriaLabel="Close"
                        onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>Close</DefaultButton>}
                        isFooterAtBottom={true}
                    >
                        <div style={{ marginBottom: "20px" }}>
                            <ThemeToggle />
                        </div>

                        <div style={{ marginBottom: "20px" }}>
                            <DefaultButton
                                onClick={clearChat}
                                disabled={!lastQuestionRef.current || isLoading}
                                text="Clear conversation"
                                style={{ width: "100%" }}
                            />
                        </div>

                        <TextField
                            className={styles.chatSettingsSeparator}
                            defaultValue={promptTemplate}
                            label="Override prompt template"
                            multiline
                            autoAdjustHeight
                            onChange={onPromptTemplateChange}
                        />

                        <Slider
                            className={styles.chatSettingsSeparator}
                            label="Temperature"
                            min={0}
                            max={1}
                            step={0.1}
                            defaultValue={temperature}
                            onChange={onTemperatureChange}
                            showValue
                            snapToStep
                        />

                        <SpinButton
                            className={styles.chatSettingsSeparator}
                            label="Retrieve this many search results:"
                            min={1}
                            max={50}
                            defaultValue={retrieveCount.toString()}
                            onChange={onRetrieveCountChange}
                        />

                        <TextField className={styles.chatSettingsSeparator} label="Exclude category" onChange={onExcludeCategoryChanged} />

                        {showSemanticRankerOption && (
                            <Checkbox
                                className={styles.chatSettingsSeparator}
                                checked={useSemanticRanker}
                                label="Use semantic ranker for retrieval"
                                onChange={onUseSemanticRankerChange}
                            />
                        )}
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useSemanticCaptions}
                            label="Use query-contextual summaries instead of whole documents"
                            onChange={onUseSemanticCaptionsChange}
                            disabled={!useSemanticRanker}
                        />
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useSuggestFollowupQuestions}
                            label="Suggest follow-up questions"
                            onChange={onUseSuggestFollowupQuestionsChange}
                        />

                        {showGPT4VOptions && (
                            <GPT4VSettings gpt4vInputs={gpt4vInput} isUseGPT4V={useGPT4V} updateUseGPT4V={setUseGPT4V} updateGPT4VInputs={setGpt4vInput} />
                        )}

                        {showVectorOption && (
                            <VectorSettings
                                showImageOptions={useGPT4V && showGPT4VOptions}
                                updateVectorFields={(options: VectorFieldOptions[]) => setVectorFieldList(options)}
                                updateRetrievalMode={(mode: RetrievalMode) => setRetrievalMode(mode)}
                            />
                        )}

                        {useLogin && (
                            <Checkbox
                                className={styles.chatSettingsSeparator}
                                checked={useOidSecurityFilter || requireAccessControl}
                                label="Use oid security filter"
                                disabled={!isLoggedIn(client) || requireAccessControl}
                                onChange={onUseOidSecurityFilterChange}
                            />
                        )}

                        {useLogin && (
                            <Checkbox
                                className={styles.chatSettingsSeparator}
                                checked={useGroupsSecurityFilter || requireAccessControl}
                                label="Use groups security filter"
                                disabled={!isLoggedIn(client) || requireAccessControl}
                                onChange={onUseGroupsSecurityFilterChange}
                            />
                        )}

                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={shouldStream}
                            label="Stream chat completion responses"
                            onChange={onShouldStreamChange}
                        />

                        {useLogin && <TokenClaimsDisplay />}
                    </Panel>
                )}

                {/* Feedback Toast */}
                {feedbackToast && <FeedbackToast message={feedbackToast.message} type={feedbackToast.type} onClose={() => setFeedbackToast(null)} />}
            </div>
        </div>
    );
};

export default Chat;
